// Event types
export interface DaemonEventMap {
    connected: undefined;
    'console-output': ConsoleOutput;
    'server-status': ServerStatus;
    'server-proc': ServerStatistics;
    'server-query': GameStatistics;
    'upload-logs': UploadedLogs;

    'upload-start': any;
    'upload-progress': any;
    'upload-complete': any;
    'upload-error': any;

    'search-results': SearchResults;
    'search-error': SearchError;

    'git-success': undefined | string;
    'git-error': string;

    'workshop-dl-status': WorkshopDownloadStatus;
    'workshop-dl-new': undefined;
    'workshop-dl-finish': undefined | [string, object?][];

    'backdoor-scanner-found': BackdoorScannerResult;
    'backdoor-scanner-finish': undefined;

    'rust-server-socket': [string, any?];
    'rust-permissions': RustPermissionsDaemonEvent;

    'install-complete': undefined;

    'transfer-logs': TransferLogs;
    'transfer-status': TransferStatus;

    'backup-completed': BackupCompleted;
    'backup-restore-completed': undefined;

    'disk-analyzer-result': ArrayBuffer;
    'disk-analyzer-status': DiskAnalyzerStatus;
    'disk-analyzer-error': string;
}

export interface DaemonActionMap {
    'send-power': PowerActionType;
    'send-command': string;
    'request-logs': undefined;
    'request-install-logs': undefined;
    'upload-logs': undefined;
    search: string | { query: string; root?: string; exclude_mounts?: boolean };
    'git-clone': GitCloneOptions;
    'git-pull': GitOptions;
    'workshop-dl-get-status': undefined;
    'workshop-dl-download': WorkshopDownloadRequest;
    'backdoor-scanner-start': undefined;
    'rust-server-socket': [string, any?];
    'rust-server-socket-disconnect': undefined;

    'disk-analyzer-start': undefined;
    'disk-analyzer-stop': undefined;
}

export type Transformers = {
    events: Record<string, TransformedDaemonEvent>;
    actions: Record<string, (...data: any) => any>;
};

export type TransformedDaemonEvent = (data: any) => [keyof DaemonEventMap, any?];

// Extraneous types
export interface RustPermissionsDaemonEvent {
    type: 'state' | 'delta' | 'op-acks' | 'import-progress' | 'import-complete';
    data?: any;
}

export type PowerActionType = 'start' | 'restart' | 'stop' | 'kill';

/* -----------------
 * Event Types
 *  ----------------- */

// console-output
export enum ConsoleMessageType {
    DAEMON,
    PROCESS,
}

export interface ConsoleOutput {
    type: ConsoleMessageType;
    line: string;
    translationData: Record<string, string>;
}

// server-status
export enum ServerStatus {
    OFF = 'offline',
    ON = 'running',
    STARTING = 'starting',
    STOPPING = 'stopping',
}

// One thread's CPU usage, as broken down by the daemon. `cpu` is the
// instantaneous usage (% of a single core) and drives the block's color;
// `weight` is max(cpu, movingAverage) and drives the block's size + ordering.
// The daemon sends these already sorted (busiest first) and noise-filtered.
export interface ThreadUsage {
    tid: number;
    name: string;
    cpu: number;
    weight: number;
}

// server-proc
export interface ServerStatistics {
    cpuUsed: number;
    memoryUsed: number;
    memoryPhysical?: number;
    diskUsed: number;
    network: {
        rxBytes: number;
        txBytes: number;
    };
    rust?: {
        fps: number;
        entityCount: number;
        fpsLimit?: number;
    };
    // Optional; the Thread Usage card hides itself when this is
    // empty/undefined.
    threads?: ThreadUsage[];
}

// server-query
export interface GameStatistics {
    type: string;
    name: string;
    description: string;
    gamemode: string;
    version: string;
    map: string;
    password: boolean;
    maxplayers: number;
    numplayers: number;
    players: any[]; // TODO: proper type for me
    bots: any[]; // TODO: proper type for me

    raw: any;
}

// upload-logs
export interface UploadedLogs {
    err?: string;
    url?: string;
}

// search-results
interface SearchResult {
    results: number;
    lines: Record<number, string>;
}

export interface SearchResults {
    tooMany: boolean;
    files: Record<string, SearchResult>;
    // Echo of the scope the search ran against, so a client multiplexing
    // searches over one socket can match results to the right requester.
    root?: string;
}

export interface SearchError {
    root?: string;
    error?: string;
}

// git-*
export interface GitOptions {
    dir: string;
    authkey?: string;
}

// git-clone
export type GitCloneOptions = GitOptions & {
    url: string;
    branch: string;
};

// workshop-dl-status
export enum WorkshopDownloadState {
    WAITING,
    DOWNLOADING,
    EXTRACTING,
    PARSING,
    WRITING,
}

interface WorkshopItem {
    name: string;
    status: [WorkshopDownloadState, object?];
}

export type WorkshopDownloadStatus = WorkshopItem[];

// workshop-dl
export interface WorkshopDownloadRequest {
    id: string;
    dir: string;
}

// backdoor-scanner-result
export enum BackdoorScannerLevel {
    LOW = 1,
    MEDIUM,
    HIGH,
    KNOWN_BACKDOOR,
}

interface BackdoorScannerDetection {
    name: string;
    description: string;
    level: BackdoorScannerLevel;
    lines: Record<number, string>;
    mainLine: number;
}

export interface BackdoorScannerResult {
    path: string;
    detections: BackdoorScannerDetection[];
}

// transfer-logs
export interface TransferLogs {
    line: string;
}

// transfer-status
export type TransferStatus = 'starting' | 'pending' | 'processing' | 'cancelling' | 'cancelled' | 'failed' | 'completed' | 'success' | 'failure';

// backup-completed
export interface BackupCompleted {
    uuid: string;
    isSuccessful: boolean;
    checksum: string;
    checksumType: string;
    fileSize: number;
}

// disk-analyzer-status
export interface DiskAnalyzerStatus {
    state: 'scanning' | 'refreshing' | 'fresh';
    detail?: string;
}
